/**
 * Admin — vendor moderation via the shared ModerationScreen pattern:
 * pin/unpin + deactivate/reactivate PATCH actions (optimistic, reverting on
 * error). Wired: GET/PATCH /api/admin/vendors.
 * Port of screens-admin.jsx AdminVendors.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { bool, num, str, useGateList } from '@/components/gate/api';
import { ModerationScreen, SmallButton } from '@/features/account-admin/bits';
import { api } from '@/lib/client';
import { AppText, Avatar, Card, Pill } from '@/ui';

type Row = { id: string; name: string; cat: string; endorsements: number; pinned: boolean; active: boolean };

function toRow(v: unknown, i: number): Row {
  return {
    id: str(v, 'id') ?? `v${i}`,
    name: str(v, 'name', 'title') ?? 'Vendor',
    cat: str(v, 'cat', 'category') ?? 'General',
    endorsements: num(v, 'endorsements', 'endorsementCount') ?? 0,
    pinned: bool(v, 'pinned', 'isPinned') ?? false,
    active: bool(v, 'active', 'isActive') ?? true,
  };
}

export default function AdminVendorsScreen() {
  const { items, setItems, state } = useGateList<unknown>('/api/admin/vendors', 'vendors');
  const rows = items.map(toRow);
  const [notice, setNotice] = React.useState<string | null>(null);

  const patchRow = (id: string, patch: Record<string, boolean>, failMsg: string) => {
    const prev = items;
    setNotice(null);
    setItems(prev.map((v, i) => (toRow(v, i).id === id ? { ...(v as object), ...patch } : v))); // optimistic
    api.patch<unknown>(`/api/admin/vendors/${id}`, patch).catch(() => {
      setItems(prev); // revert
      setNotice(failMsg);
    });
  };

  return (
    <ModerationScreen
      title="Vendors"
      createLabel="Add vendor"
      onCreate={() => router.push('/admin/create-vendor')}
      state={state}
      count={rows.length}
      emptyIcon="wrench"
      emptyTitle="No vendors yet"
      emptyBody="Add trusted electricians, plumbers and services. Tap + to add the first vendor."
      notice={notice}
    >
      {rows.map((v) => (
        <Card key={v.id} style={!v.active ? { opacity: 0.6 } : undefined}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
            <Avatar name={v.name} size={44} />
            <View style={{ flex: 1, minWidth: 0 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                <AppText variant="bodyMd" weight="500">{v.name}</AppText>
                {!v.active ? <Pill status="rejected" label="OFF" /> : v.pinned ? <Pill status="approved" label="PINNED" /> : null}
              </View>
              <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                {v.cat} · {v.endorsements} endorsements
              </AppText>
            </View>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2, marginTop: space.s3 }}>
            <SmallButton
              label={v.pinned ? 'Unpin' : 'Pin'}
              icon="pin"
              active={v.pinned}
              disabled={!v.active}
              onPress={() => patchRow(v.id, { pinned: !v.pinned }, 'Could not update the pin. Try again.')}
            />
            <View style={{ flex: 1 }} />
            <SmallButton
              label={v.active ? 'Deactivate' : 'Reactivate'}
              danger={v.active}
              onPress={() => patchRow(v.id, { isActive: !v.active }, 'Could not update the vendor. Try again.')}
            />
          </View>
        </Card>
      ))}
    </ModerationScreen>
  );
}
